import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import ReferralShareButtons from "./ReferralShareButtons";
import { copyText } from "../../utils/copyText";
import { hasCreatorAccess } from "../../utils/roleUtils";
import {
  getCreatorJoinLink,
  getReferralCode,
  getReferralDisplayHost,
  getReferralLink,
  getReferralStats,
} from "../../services/referralService";
import { describeCreatorFeeStatus, getCreatorReferralBadgeStates } from "../../services/feeLadder";
import { getFanReferralBadgeStates } from "../../constants/fanReferralBadges";
import "../../screens/Referral.css";

function BadgeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v10M9 9.5c0-1.4 1.3-2.5 3-2.5s3 1.1 3 2.3c0 2.7-6 1.3-6 4 0 1.2 1.3 2.2 3 2.2s3-1 3-2.2" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function CopiedIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function LinkCard({ label, display, url, note, copied, onCopy, shareKind, displayName, onToast }) {
  return (
    <div className={`rf-link-card${shareKind === "creator-join" ? " secondary" : ""}`}>
      <div className="rf-link-label">{label}</div>
      <div className="rf-link-row">
        <div className="rf-link-display">{display}</div>
        <button type="button" className={`rf-copy-btn${copied ? " copied" : ""}`} onClick={onCopy}>
          {copied ? <CopiedIcon /> : <CopyIcon />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {note && <div className="rf-link-note">{note}</div>}
      <ReferralShareButtons url={url} kind={shareKind} displayName={displayName} onToast={onToast} />
    </div>
  );
}

function BadgeLadder({ badges }) {
  return (
    <div className="rf-badge-ladder">
      {badges.map((badge) => (
        <div key={badge.id} className={`rf-badge ${badge.state === "earned" ? "earned" : ""} ${badge.state === "current" ? "current" : ""}`}>
          <div className="rf-badge-icon"><BadgeIcon /></div>
          <div className="rf-badge-name">{badge.label}</div>
          <div className="rf-badge-thresh">{badge.threshold}</div>
        </div>
      ))}
    </div>
  );
}

function ReferralTools({ onClose }) {
  const { currentUser, userProfile } = useAuth();
  const isCreator = hasCreatorAccess(userProfile);
  const [code, setCode] = useState(userProfile?.referralCode || userProfile?.username || "");
  const [stats, setStats] = useState({ total: 0, unlocked: 0, creditsEarned: 0 });
  const [copiedKey, setCopiedKey] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    let cancelled = false;
    getReferralCode(currentUser.uid).then((next) => {
      if (!cancelled && next) setCode(next);
    });
    getReferralStats(currentUser.uid).then((next) => {
      if (!cancelled) setStats(next);
    });
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  const showToast = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2200);
  };

  const host = getReferralDisplayHost();
  const fanUrl = getReferralLink(code);
  const creatorUrl = getCreatorJoinLink(code);
  const fanDisplay = code ? `${host}/r/${code}` : `${host}/r/…`;
  const creatorDisplay = code ? `${host}/creators/join?ref=${code}` : `${host}/creators/join`;

  const handleCopy = async (key, url) => {
    const ok = await copyText(url);
    if (!ok) return;
    setCopiedKey(key);
    showToast("Link copied to clipboard");
    window.setTimeout(() => setCopiedKey(""), 1800);
  };

  const feeStatus = useMemo(
    () => (isCreator ? describeCreatorFeeStatus(userProfile || {}) : null),
    [isCreator, userProfile]
  );

  const creatorBadgeCount = Number(userProfile?.lifetimeCreatorReferrals) || 0;
  const creatorBadges = useMemo(
    () => getCreatorReferralBadgeStates(creatorBadgeCount).badges,
    [creatorBadgeCount]
  );
  const fanBadges = useMemo(
    () => getFanReferralBadgeStates(stats.total).badges,
    [stats.total]
  );

  if (!currentUser) {
    return (
      <div className="rf-page">
        <div className="rf-signed-out">
          <h1>Bring your people.</h1>
          <p>Sign in to get your referral link and start earning credits.</p>
          <Link to="/welcome?auth=signin" onClick={onClose}>Sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="rf-page">
      {isCreator ? (
        <>
          <div className="rf-hero">
            <div className="rf-hero-kicker">Your Referral Tools</div>
            <h1 className="rf-hero-title">Grow your audience, <em>and your rate.</em></h1>
            <p className="rf-hero-sub">
              Two ways to refer — bring fans to follow you, or bring other creators to the platform and move up the fee tier ladder.
            </p>
          </div>

          <LinkCard
            label="Refer a fan"
            display={fanDisplay}
            url={fanUrl}
            note="They auto-follow you on signup — no revenue share, ever"
            copied={copiedKey === "fan"}
            onCopy={() => handleCopy("fan", fanUrl)}
            shareKind="creator-fan"
            displayName={userProfile?.displayName}
            onToast={showToast}
          />
          <LinkCard
            label="Refer a creator"
            display={creatorDisplay}
            url={creatorUrl}
            note="Counts once they're approved and fill their first drop — moves you up the fee ladder"
            copied={copiedKey === "creator"}
            onCopy={() => handleCopy("creator", creatorUrl)}
            shareKind="creator-join"
            displayName={userProfile?.displayName}
            onToast={showToast}
          />

          {feeStatus && (
            <div className="rf-tier-card">
              <div className="rf-tier-icon" aria-hidden>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 18h14M5 18l1.5-9L9 12l3-6 3 6 2.5-3L19 18" />
                </svg>
              </div>
              <div>
                <div className="rf-tier-title">{feeStatus.name} · {feeStatus.rate}</div>
                <div className="rf-tier-sub">
                  {feeStatus.note}{" "}
                  <strong>{feeStatus.count} creator{feeStatus.count === 1 ? "" : "s"}</strong> referred so far.
                </div>
              </div>
            </div>
          )}

          <div className="rf-stats">
            <div className="rf-stat-box">
              <div className="rf-stat-num">{stats.total}</div>
              <div className="rf-stat-lbl">Fans joined</div>
            </div>
            <div className="rf-stat-box">
              <div className="rf-stat-num">{creatorBadgeCount}</div>
              <div className="rf-stat-lbl">Creators referred</div>
            </div>
            <div className="rf-stat-box">
              <div className="rf-stat-num">{stats.creditsEarned}</div>
              <div className="rf-stat-lbl">Credits earned</div>
            </div>
          </div>

          <div className="rf-section-label">Creator Referral Badges — {creatorBadgeCount} of 100</div>
          <BadgeLadder badges={creatorBadges} />

          <div className="rf-how">
            <div className="rf-hiw-row">
              <div className="rf-hiw-icon"><CheckIcon /></div>
              <div>
                <div className="rf-hiw-title">15 referrals locks your fee at 12%</div>
                <div className="rf-hiw-desc">Founding Recruiter is permanent — one-way, no decay.</div>
              </div>
            </div>
            <div className="rf-hiw-row">
              <div className="rf-hiw-icon"><CheckIcon /></div>
              <div>
                <div className="rf-hiw-title">50 referrals — Kingmaker, 11%</div>
                <div className="rf-hiw-desc">One of the rarest badges on the platform.</div>
              </div>
            </div>
            <div className="rf-hiw-row">
              <div className="rf-hiw-icon"><CheckIcon /></div>
              <div>
                <div className="rf-hiw-title">100 referrals — The Architect</div>
                <div className="rf-hiw-desc">10% fee, a platform-wide announcement, and a physical plaque.</div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="rf-hero">
            <div className="rf-hero-kicker">Your Referral Link</div>
            <h1 className="rf-hero-title">Bring your people, <em>earn credits.</em></h1>
            <p className="rf-hero-sub">
              When someone joins through your link, you both come out ahead — +50 credits each after their first drop unlock. No cash value, just credits and a little recognition.
            </p>
          </div>

          <LinkCard
            label="Share this link"
            display={fanDisplay}
            url={fanUrl}
            note="+50 credits for each of you after they unlock their first drop"
            copied={copiedKey === "fan"}
            onCopy={() => handleCopy("fan", fanUrl)}
            shareKind="fan"
            displayName={userProfile?.displayName}
            onToast={showToast}
          />

          <div className="rf-stats">
            <div className="rf-stat-box">
              <div className="rf-stat-num">{stats.total}</div>
              <div className="rf-stat-lbl">Joined</div>
            </div>
            <div className="rf-stat-box">
              <div className="rf-stat-num">{stats.unlocked}</div>
              <div className="rf-stat-lbl">Unlocked</div>
            </div>
            <div className="rf-stat-box">
              <div className="rf-stat-num">{stats.creditsEarned}</div>
              <div className="rf-stat-lbl">Credits earned</div>
            </div>
          </div>

          <div className="rf-section-label">Referral Badges — {stats.total} of 1,000</div>
          <BadgeLadder badges={fanBadges} />

          <div className="rf-how">
            <div className="rf-hiw-row">
              <div className="rf-hiw-icon"><CheckIcon /></div>
              <div>
                <div className="rf-hiw-title">They sign up through your link</div>
                <div className="rf-hiw-desc">Takes about a minute — no credit card needed to browse.</div>
              </div>
            </div>
            <div className="rf-hiw-row">
              <div className="rf-hiw-icon"><CheckIcon /></div>
              <div>
                <div className="rf-hiw-title">They unlock their first drop</div>
                <div className="rf-hiw-desc">Credits land after that first unlock — not at signup.</div>
              </div>
            </div>
            <div className="rf-hiw-row">
              <div className="rf-hiw-icon"><CheckIcon /></div>
              <div>
                <div className="rf-hiw-title">You both earn credits</div>
                <div className="rf-hiw-desc">+50 credits each. No cap, no catch.</div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className={`rf-toast${toast ? " show" : ""}`} role="status">
        <CheckIcon />
        <span>{toast}</span>
      </div>
    </div>
  );
}

export default function ReferralOverlay({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="rf-overlay" onClick={onClose} role="presentation">
      <div
        className="rf-overlay-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rf-overlay-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="rf-overlay-close" onClick={onClose} aria-label="Close referral tools">
          ✕
        </button>
        <div id="rf-overlay-title" className="rf-sr-only">Referral tools</div>
        <ReferralTools onClose={onClose} />
      </div>
    </div>
  );
}
